import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {Dispatch, SetStateAction, useEffect, useState} from "react";
import {useSurveyReviewRefresh} from "../shared/surveyQueryRefresh";

export interface SurveySubmissionReview {
    reviewState: SurveyReviewState;
    setReviewState: Dispatch<SetStateAction<SurveyReviewState>>;
    reviewNotes: string;
    setReviewNotes: Dispatch<SetStateAction<string>>;
    /** The form differs from what the server has. */
    dirty: boolean;
    saving: boolean;
    approving: boolean;
    /** The one-click resolve is offered: the host takes the hand-over, and the server still has the reply open. */
    canApprove: boolean;
    save: () => Promise<void>;
    markReviewed: () => Promise<void>;
}

interface Args {
    submission: AggregationSurveySubmissionListItem;
    readOnly: boolean;
    onReviewed?: () => void;
}

/** A reply's review form: the state and notes being edited, Save, and the one-click resolve. */
export function useSurveySubmissionReview({submission, readOnly, onReviewed}: Args): SurveySubmissionReview {
    const {t} = useLocalize();
    const refreshReviewData = useSurveyReviewRefresh();

    const [reviewState, setReviewState] = useState<SurveyReviewState>(
        submission.reviewState ?? SurveyReviewState.OPEN
    );
    const [reviewNotes, setReviewNotes] = useState(submission.reviewNotes ?? '');
    const [saving, setSaving] = useState(false);
    const [approving, setApproving] = useState(false);

    useEffect(() => {
        setReviewState(submission.reviewState ?? SurveyReviewState.OPEN);
        setReviewNotes(submission.reviewNotes ?? '');
    }, [submission.id, submission.reviewState, submission.reviewNotes]);

    const dirty = reviewState !== (submission.reviewState ?? SurveyReviewState.OPEN)
        || reviewNotes !== (submission.reviewNotes ?? '');

    const save = async () => {
        setSaving(true);
        try {
            await SurveyService.reviewSubmission({
                id: submission.id,
                reviewState,
                reviewNotes: reviewNotes.trim() || undefined,
            });
            await refreshReviewData();
            enqueueSnackbar(t('Changes saved!'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } catch (e) {
            console.log(e);
            enqueueSnackbar(t('Changes could not be saved. Check all fields!'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setSaving(false);
        }
    };

    const canApprove = !!onReviewed && !readOnly
        && (submission.reviewState ?? SurveyReviewState.OPEN) !== SurveyReviewState.REVIEWED;

    const markReviewed = async () => {
        setApproving(true);
        try {
            await SurveyService.reviewSubmission({
                id: submission.id,
                reviewState: SurveyReviewState.REVIEWED,
                reviewNotes: reviewNotes.trim() || undefined,
            });
            // Hand over before the lists refresh: with the Open filter on, this reply leaves the
            // list, and the next one has to be picked while it is still known where it sits.
            onReviewed?.();
            enqueueSnackbar(t('Marked as resolved'), {
                variant: 'success',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
            void refreshReviewData();
        } catch (e) {
            console.log(e);
            enqueueSnackbar(t('The status could not be changed.'), {
                variant: 'error',
                anchorOrigin: {vertical: 'bottom', horizontal: 'center'}
            });
        } finally {
            setApproving(false);
        }
    };

    return {
        reviewState,
        setReviewState,
        reviewNotes,
        setReviewNotes,
        dirty,
        saving,
        approving,
        canApprove,
        save,
        markReviewed,
    };
}
